import { Button, Card, Input, Radio, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import ShipMonitorModal, {
    type ShipMonitorDevice,
    type ShipMonitorMapShip
} from "@/views/pages/logistics/shipManagement/ShipMonitorModal";
import { shipService } from "@/api/services/shipService";
import { DEVICE_TYPE, ONLINE } from "@/meta/logistics/device";
import { AIS_SHIP_TYPE } from "@/meta/logistics/shipInfo";
import { dateTimeFormat } from "@/utils";
import { useUserStore } from "@/views/stores";
import { PARTNERSHIP, TRANSPORT_CAPACITY_STATE } from "@zhihaoscm/meta";
import type { TransportCapacityDevice, TransportCapacityRow } from "./TransportCapacityManagementPage";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

export interface TransportCapacityDetailPageProps {
    capacityId?: string;
    loadCapacity?: (id: string) => Promise<TransportCapacityRow>;
    unbindCapacity?: (id: string) => Promise<unknown>;
    reviewCapacity?: (id: string, payload: { state: number; rejectReason?: string }) => Promise<unknown>;
    loadMonitorShip?: (id: string) => Promise<{ ship?: Partial<ShipMonitorMapShip>; devices?: ShipMonitorDevice[] }>;
    canManage?: boolean;
    banButton?: boolean;
}
/** 复用 Vue 运力详情 VO 接口。 */
export async function loadTransportCapacityDetail(id: string) {
    const response = await shippingRequestHttp.service.get(`/owner-ship/vo/${id}`);
    return response.data as TransportCapacityRow;
}
/** 复用 Vue 运力解绑接口。 */
export async function unbindTransportCapacityDetail(id: string) {
    const response = await shippingRequestHttp.service.put(`/owner-ship/unbind/${id}`);
    return response.data;
}
/** 复用 Vue 运力审核接口：通过为 4，驳回为 3。 */
export async function reviewTransportCapacityDetail(id: string, payload: { state: number; rejectReason?: string }) {
    const response = await shippingRequestHttp.service.put(`/owner-ship/review/${id}`, payload);
    return response.data;
}
/** 复用旧 Vue openMonitor 的船舶信息查询，供详情页监控弹窗使用。 */
export async function loadTransportCapacityDetailMonitorShip(id: string) {
    return (await shipService.getInfo(id)) as unknown as {
        ship?: Partial<ShipMonitorMapShip>;
        devices?: ShipMonitorDevice[];
    };
}
const value = (v?: unknown) => (v == null || v === "" ? "--" : String(v));
const formatShipDimensions = (length?: number, width?: number) => {
    if (!length && !width) return "--/--";
    return `${length || "--"} | ${width ? `${width}米` : "--"}`;
};
const formatWithUnit = (item?: number) => (item === undefined || item === null ? "--" : `${item}米`);
const formatDate = (item?: string) => (item ? dateTimeFormat(item) : "--");

/** 运力详情接口中证件字段的最小展示映射。 */
type TransportCapacityCertificateFiles = {
    shipNationalityCertificateFile?: number;
    shipInspectionReportFile?: number;
    shipBusinessTransportCertificateFile?: number;
};

/** 兼容旧 Vue 详情返回的设备字段，不改变列表接口契约。 */
type TransportCapacityDetail = Omit<TransportCapacityRow, "device"> & {
    ownerShip: TransportCapacityRow["ownerShip"] & {
        certificateFiles?: TransportCapacityCertificateFiles;
    };
    device?: TransportCapacityDevice & {
        type?: number;
        serialNo?: string;
        position?: string;
    };
};

/** 物流管理-运力详情，按 Vue 状态展示证件/设备/船主信息及解绑入口。 */
export function TransportCapacityDetailPage({
    capacityId,
    loadCapacity = loadTransportCapacityDetail,
    unbindCapacity = unbindTransportCapacityDetail,
    reviewCapacity = reviewTransportCapacityDetail,
    loadMonitorShip = loadTransportCapacityDetailMonitorShip,
    canManage,
    banButton
}: TransportCapacityDetailPageProps) {
    const storeCanManage = useUserStore(state => state.hasAnyPermission(["ROLE_CM_M"]));
    // 对齐旧 Vue userStore.banButton，同时允许宿主显式传入封禁状态。
    const storeBanButton = useUserStore(state => state.banButton);
    const mayManage = canManage ?? storeCanManage;
    const isButtonBanned = banButton ?? storeBanButton;
    const params = useParams<{ id: string }>();
    const navigate = useNavigate();
    const id = capacityId ?? params.id ?? "";
    const [detail, setDetail] = useState<TransportCapacityDetail>();
    const [errorMessage, setErrorMessage] = useState("");
    const [unbindOpen, setUnbindOpen] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewResult, setReviewResult] = useState<1 | 2>(1);
    const [rejectReason, setRejectReason] = useState("");
    const [reviewError, setReviewError] = useState("");
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [monitorOpen, setMonitorOpen] = useState(false);
    const [monitorShip, setMonitorShip] = useState<{
        ship?: Partial<ShipMonitorMapShip>;
        devices?: ShipMonitorDevice[];
    }>();
    const [monitorDevice, setMonitorDevice] = useState<ShipMonitorDevice>();
    const [monitorError, setMonitorError] = useState("");
    useEffect(() => {
        let disposed = false;
        if (!id) return;
        void loadCapacity(id)
            .then(result => {
                if (!disposed) setDetail(result as TransportCapacityDetail);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("运力详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadCapacity]);
    /** 打开审核弹窗时恢复 Vue 页面默认的“同意”选项和空备注。 */
    const openReview = () => {
        setReviewResult(1);
        setRejectReason("");
        setReviewError("");
        setErrorMessage("");
        setReviewOpen(true);
    };
    /** 按审核结果组装状态参数，成功后刷新详情保持 Vue 行为。 */
    const submitReview = async () => {
        if (!id) return;
        if (reviewResult === 2 && !rejectReason.trim()) {
            setReviewError("请输入驳回原因");
            return;
        }
        setReviewSubmitting(true);
        try {
            const payload = reviewResult === 1 ? { state: 4 } : { state: 3, rejectReason: rejectReason.trim() };
            await reviewCapacity(id, payload);
            setReviewOpen(false);
            notifyOperationSuccess(reviewResult === 1 ? "审核通过" : "审核驳回");
            await loadCapacity(id);
            setErrorMessage("");
        } catch {
            setErrorMessage("审核失败");
        } finally {
            setReviewSubmitting(false);
            setReviewError("");
        }
    };
    // 复用 Vue 详情页解绑行为，成功后关闭弹窗，失败时保留详情上下文并提示错误。
    const confirmUnbind = async () => {
        if (!id) return;
        try {
            await unbindCapacity(id);
            notifyOperationSuccess("解绑成功");
            setUnbindOpen(false);
        } catch {
            setErrorMessage("解绑失败");
        }
    };
    /** 按 Vue 详情页 openMonitor 行为加载船舶 VO，再打开监控调阅弹窗。 */
    const openMonitor = async () => {
        const shipId = detail?.ship?.id;
        if (!shipId) {
            setMonitorError("监控加载失败");
            return;
        }
        try {
            const result = await loadMonitorShip(String(shipId));
            setMonitorShip(result);
            setMonitorDevice(result.devices?.[0]);
            setMonitorOpen(true);
            setMonitorError("");
        } catch {
            setMonitorError("监控加载失败");
        }
    };
    const state = detail?.ownerShip.state;
    // 详情数据保持与旧 Vue VO 相同的证件和设备字段读取方式。
    const certificateFiles = detail?.ownerShip.certificateFiles;
    const device = detail?.device;
    return (
        <section aria-label="transport-capacity-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhDetailHeader
                    rightInfos={[{ label: "创建时间", value: formatDate(detail?.ownerShip.createdTime) }]}
                    stateDesc={TRANSPORT_CAPACITY_STATE.getIntroByCode(state) || "--"}
                    title="运力详情"
                />
                {state === 3 || state === 5 ? (
                    <p>
                        {state === 3 ? "驳回原因" : "作废原因"}：{value(detail?.ownerShip.rejectReason)}
                    </p>
                ) : null}
            </Card>
            {errorMessage ? <Alert role="alert" type="error" message={errorMessage} /> : null}
            {detail ? (
                <ZhMultiGrid>
                    <div className="section-card">
                        <div className="section-title">船舶基本信息</div>
                        {/* 使用 Vue 详情页同样的四列信息网格，保持字段顺序和默认值契约。 */}
                        <ZhGrid cols={4} gap={[16, 20]}>
                            <ZhBaseItem label="中文船名" value={value(detail.ship?.cnname)} />
                            <ZhBaseItem label="英文船名" value={value(detail.ship?.name)} />
                            <ZhBaseItem label="类型" value={AIS_SHIP_TYPE.getIntroByCode(detail.ship?.type) || "--"} />
                            <ZhBaseItem label="型深" value={formatWithUnit(detail.ship?.deep)} />
                            <ZhBaseItem label="MMSI" value={value(detail.ship?.id)} />
                            <ZhBaseItem label="IMO" value={value(detail.ship?.imo)} />
                            <ZhBaseItem label="呼号" value={value(detail.ship?.callSign)} />
                            <ZhBaseItem
                                label="船长/宽"
                                value={formatShipDimensions(detail.ship?.length, detail.ship?.width)}
                            />
                            <ZhBaseItem label="吃水" value={formatWithUnit(detail.ship?.draft)} />
                        </ZhGrid>
                    </div>
                    <div className="section-card">
                        <div className="section-title">绑定信息</div>
                        {/* 绑定关系继续读取旧 Vue API 的 ownerShip.partnership 枚举值。 */}
                        <ZhGrid cols={4} gap={[16, 20]}>
                            <ZhBaseItem label="货主" value={value(detail.owner?.institutionName)} />
                            <ZhBaseItem
                                label="合作关系"
                                value={PARTNERSHIP.getIntroByCode(detail.ownerShip.partnership) || "--"}
                            />
                        </ZhGrid>
                    </div>
                    <div className="section-card">
                        <div className="section-title">证件信息</div>
                        {/* 证件文件保留 Vue VO 字段，仅做多列详情展示，不引入媒体预览。 */}
                        <div className="grid grid-cols-4 gap-x-4 gap-y-5">
                            <ZhBaseItem
                                label="船舶国籍证书"
                                value={value(certificateFiles?.shipNationalityCertificateFile)}
                            />
                            <ZhBaseItem
                                label="船舶校验证书"
                                value={value(certificateFiles?.shipInspectionReportFile)}
                            />
                            <ZhBaseItem
                                label="船舶营运证"
                                value={value(certificateFiles?.shipBusinessTransportCertificateFile)}
                            />
                        </div>
                    </div>
                    {state === 4 && mayManage ? (
                        <>
                            <div className="section-card">
                                <div className="section-title">设备信息</div>
                                {device ? (
                                    <div className="grid grid-cols-4 gap-x-4 gap-y-5">
                                        <ZhBaseItem
                                            label="设备名称"
                                            // 以节点传值避免 beta.13 测试替身同时输出值节点和辅助精确值。
                                            value={<span>{value(device.name)}</span>}
                                        />
                                        <ZhBaseItem
                                            label="设备类型"
                                            value={DEVICE_TYPE.getIntroByCode(device.type) || "--"}
                                        />
                                        <ZhBaseItem label="设备序列号" value={value(device.serialNo)} />
                                        <ZhBaseItem label="设备位置" value={value(device.position)} />
                                        <ZhBaseItem
                                            label="设备状态"
                                            value={ONLINE.getIntroByCode(device.online) || "--"}
                                        />
                                    </div>
                                ) : (
                                    <div>船舶暂未申领设备</div>
                                )}
                            </div>
                            <div className="section-card">
                                <div className="section-title">船主信息</div>
                                <div className="grid grid-cols-4 gap-x-4 gap-y-5">
                                    {/* 保留旧 Vue 详情中的独立船主值节点，兼容详情文本检索契约。 */}
                                    <div>
                                        <span>绑定船主：</span>
                                        <span>{value(detail.captain?.realName)}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null}
                </ZhMultiGrid>
            ) : null}
            <Card>
                <Space>
                    {state === 2 && mayManage ? (
                        <Button aria-label="审核" disabled={isButtonBanned} type="primary" onClick={openReview}>
                            审核
                        </Button>
                    ) : null}
                    {state === 4 ? (
                        <Button
                            aria-label="解绑"
                            disabled={isButtonBanned}
                            type="primary"
                            onClick={() => setUnbindOpen(true)}
                        >
                            解绑
                        </Button>
                    ) : null}
                    {state === 4 ? (
                        <Button
                            aria-label="监控"
                            disabled={isButtonBanned}
                            type="primary"
                            onClick={() => void openMonitor()}
                        >
                            监控
                        </Button>
                    ) : null}
                    <Button aria-label="返回" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Card>
            <ZhDialog
                open={reviewOpen}
                title="运力审核"
                onCancel={() => setReviewOpen(false)}
                width={520}
                footer={
                    <Space>
                        <Button aria-label="取消" disabled={reviewSubmitting} onClick={() => setReviewOpen(false)}>
                            取消
                        </Button>
                        <Button
                            aria-label="确认审核"
                            loading={reviewSubmitting}
                            type="primary"
                            onClick={() => void submitReview()}
                        >
                            确认审核
                        </Button>
                    </Space>
                }
            >
                <Space direction="vertical" className="w-full">
                    <span>货主：{value(detail?.owner?.institutionName)}</span>
                    <span>船舶：{value(detail?.ship?.cnname || detail?.ship?.name)}</span>
                    <span>合作关系：{PARTNERSHIP.getIntroByCode(detail?.ownerShip.partnership) || "--"}</span>
                    <Radio.Group
                        aria-label="审核意见"
                        value={reviewResult}
                        onChange={event => setReviewResult(event.target.value as 1 | 2)}
                    >
                        <Radio value={1}>同意</Radio>
                        <Radio value={2}>驳回</Radio>
                    </Radio.Group>
                    {reviewResult === 2 ? (
                        <Input.TextArea
                            aria-label="驳回原因"
                            value={rejectReason}
                            onChange={event => setRejectReason(event.target.value)}
                            placeholder="请输入50字以内的拒绝原因"
                            maxLength={50}
                            showCount
                            rows={3}
                        />
                    ) : null}
                    {reviewError ? <Alert role="alert" type="error" message={reviewError} /> : null}
                </Space>
            </ZhDialog>
            <ZhDialog
                open={unbindOpen}
                title="解除绑定"
                onCancel={() => setUnbindOpen(false)}
                width={420}
                footer={
                    <Space>
                        <Button aria-label="取消" onClick={() => setUnbindOpen(false)}>
                            取消
                        </Button>
                        <Button aria-label="确认解绑" type="primary" onClick={() => void confirmUnbind()}>
                            确认解绑
                        </Button>
                    </Space>
                }
            >
                确认解除当前运力绑定？
            </ZhDialog>
            {monitorError ? <Alert role="alert" type="error" message={monitorError} /> : null}
            <ShipMonitorModal
                open={monitorOpen}
                device={monitorDevice}
                shipId={String(detail?.ship?.id || "")}
                ship={monitorShip?.ship}
                onCancel={() => setMonitorOpen(false)}
            />
        </section>
    );
}

export default TransportCapacityDetailPage;
